import { API_BASE_URL, ApiResponse } from '../config/supabase';
import {
  MetalRatesResponse,
  AutoFetchResponse
} from '../types/donation';

class LiveRatesService {
  private baseUrl = `${API_BASE_URL}/live-rates`;

  // Generic error handler
  private handleError(error: any, operation: string): any {
    console.error(`❌ Live Rates API error in ${operation}:`, error);
    return {
      success: false,
      error: error.message || 'An error occurred',
      details: error
    };
  }

  // Generic success response
  private handleSuccess<T>(data: T, operation: string): any {
    console.log(`✅ Live Rates API ${operation} successful`);
    return {
      success: true,
      data: data
    };
  }

  // ==================== RATES MANAGEMENT ====================

  // Get current rates with expiration info
  async getCurrentRates(): Promise<MetalRatesResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/current`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get current live rates');
      }
      
      return this.handleSuccess(result.data, 'get current live rates');
    } catch (error) {
      return this.handleError(error, 'get current live rates');
    }
  }

  // Fetch rates manually
  async fetchRates(): Promise<MetalRatesResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch live rates');
      }
      
      return this.handleSuccess(result.data, 'fetch live rates');
    } catch (error) {
      return this.handleError(error, 'fetch live rates');
    }
  }

  // ==================== AUTO-FETCH MANAGEMENT ====================

  // Start auto-fetch
  async startAutoFetch(intervalSeconds: number = 30): Promise<AutoFetchResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/start-auto-fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ interval: intervalSeconds })
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to start live rates auto-fetch');
      }
      
      return this.handleSuccess(result.data, 'start live rates auto-fetch');
    } catch (error) {
      return this.handleError(error, 'start live rates auto-fetch');
    }
  }

  // Stop auto-fetch
  async stopAutoFetch(): Promise<AutoFetchResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/stop-auto-fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to stop live rates auto-fetch');
      }
      
      return this.handleSuccess(result.data, 'stop live rates auto-fetch');
    } catch (error) {
      return this.handleError(error, 'stop live rates auto-fetch');
    }
  }

  // Get auto-fetch status
  async getAutoFetchStatus(): Promise<AutoFetchResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/status`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get live rates auto-fetch status');
      }
      
      return this.handleSuccess(result.data, 'get live rates auto-fetch status');
    } catch (error) {
      return this.handleError(error, 'get live rates auto-fetch status');
    }
  }

  // ==================== UTILITY METHODS ====================

  // Format rate for display
  formatRate(rate: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(rate);
  }

  // Format time left
  formatTimeLeft(minutes: number, seconds: number): string {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // Get status color
  getStatusColor(isExpired: boolean, willExpireSoon: boolean): string {
    if (isExpired) return 'text-red-500';
    if (willExpireSoon) return 'text-yellow-500';
    return 'text-green-500';
  }

  // Get status icon
  getStatusIcon(isExpired: boolean, willExpireSoon: boolean): string {
    if (isExpired) return '🔴';
    if (willExpireSoon) return '🟡';
    return '🟢';
  }

  // Get metal display info
  getMetalDisplayInfo(metalType: string) {
    const metals = {
      gold: {
        name: 'Gold',
        symbol: 'Au',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: '🟡',
        unit: 'grams',
        description: 'Pure gold for investment and donation'
      },
      silver: {
        name: 'Silver',
        symbol: 'Ag',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        icon: '⚪',
        unit: 'grams',
        description: 'Pure silver for investment and donation'
      },
      platinum: {
        name: 'Platinum',
        symbol: 'Pt',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        icon: '🔵',
        unit: 'grams',
        description: 'Pure platinum for investment and donation'
      }
    };

    return metals[metalType as keyof typeof metals] || metals.gold;
  }

  // Calculate metal quantity from amount
  calculateMetalQuantity(amount: number, rate: number): number {
    if (rate <= 0) return 0;
    return Math.round((amount / rate) * 1000) / 1000; // Round to 3 decimal places
  }

  // Calculate amount from metal quantity
  calculateAmount(metalQuantity: number, rate: number): number {
    return Math.round(metalQuantity * rate * 100) / 100; // Round to 2 decimal places
  }

  // Validate amount
  validateAmount(amount: number): boolean {
    return amount > 0 && amount <= 1000000; // Max 10 lakhs
  }

  // Validate metal quantity
  validateMetalQuantity(quantity: number): boolean {
    return quantity > 0 && quantity <= 1000; // Max 1 kg
  }

  // Get donation type info
  getDonationTypeInfo(type: string) {
    const types = {
      gold: {
        name: 'Gold Donation',
        description: 'Donate pure gold for temple development',
        benefits: [
          'Pure 24K gold investment',
          'Secure digital storage',
          'Regular market updates',
          'Easy redemption process'
        ],
        icon: '🟡',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50'
      },
      silver: {
        name: 'Silver Donation',
        description: 'Donate pure silver for temple development',
        benefits: [
          'Pure silver investment',
          'Affordable entry point',
          'Regular market updates',
          'Easy redemption process'
        ],
        icon: '⚪',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50'
      },
      platinum: {
        name: 'Platinum Donation',
        description: 'Donate pure platinum for temple development',
        benefits: [
          'Premium platinum investment',
          'High value storage',
          'Regular market updates',
          'Easy redemption process'
        ],
        icon: '🔵',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
      }
    };

    return types[type as keyof typeof types] || types.gold;
  }

  // Get payment method info
  getPaymentMethodInfo(method: string) {
    const methods = {
      upi: {
        name: 'UPI Payment',
        description: 'Pay using UPI apps like Google Pay, PhonePe, Paytm',
        icon: '📱',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        instructions: [
          'Open your UPI app',
          'Scan the QR code or use payment link',
          'Enter amount and complete payment',
          'Payment will be processed automatically'
        ]
      },
      card: {
        name: 'Card Payment',
        description: 'Pay using credit or debit card',
        icon: '💳',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        instructions: [
          'Enter card details',
          'Complete 3D Secure if required',
          'Payment will be processed automatically'
        ]
      },
      netbanking: {
        name: 'Net Banking',
        description: 'Pay using your bank account',
        icon: '🏦',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        instructions: [
          'Select your bank',
          'Login to your net banking',
          'Complete the payment',
          'Payment will be processed automatically'
        ]
      }
    };

    return methods[method as keyof typeof methods] || methods.upi;
  }
}

export const liveRatesService = new LiveRatesService(); 